import Modal from "@/components/controls/Modal";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { Close } from "@dreamhub/designsystem/icons";
import toast from "react-hot-toast";
import CreateCompanyModal from "@/pages/companies/components/CreateCompanyModal";
import CreateNewOptionModal from "@/components/controls/CreateNewOptionModal";
import { UserData } from "@/entities/users/types";
import {
  PersonData,
  PersonReferenceabilityLevel,
  PersonStatus
} from "@/entities/people/types";
import {
  useCreatePeopleMutation,
  useCreatePersonWithDreamerMutation
} from "@/entities/people/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { mapEmptyToNull } from "@/utils/formatting";
import { useEntityFields, useUpdateEntityField } from "@/pages/settings/hooks";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext,
  buildMapEmptyToNullObject
} from "@/utils/entityFieldsUtils/formFields";
import useBoundStore from "@/services/store/BoundStore";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import { createUnsavedChangesModalSetter } from "@/entities/modals/helpers";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { useNavigate } from "react-router-dom";
import Spinner from "@/components/ui/spinner";
import { cn } from "@/utils/utils";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import DreamerModeTabs, {
  DREAMER_MODE,
  MANUAL_MODE,
  DreamerMode
} from "@/components/controls/DreamerModeTabs";
import CreatePersonWithDreamer from "./CreatePersonWithDreamer";
import { emailRegex } from "@/utils/validation";
import { isAxiosError } from "axios";
import { useCheckCompanyDuplicates } from "@/pages/companies/hooks";
import { extractDomain, isValidWebsite } from "@/utils/domains";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetPage?: () => void;
  companyId?: string;
  /** Standalone People page only — embedded callers need the synchronous `onSuccess(id)` the async Dreamer path can't give. */
  allowDreamer?: boolean;
  onSuccess?: (newPersonId: string, name: string) => void;
  defaultFirstName?: string;
  defaultLastName?: string;
  /** Prefill from a deep link, e.g. the enrichment-failure notification's manual-create CTA. */
  defaultEmail?: string;
  onCandidateDataConsumed?: () => void;
}

const CreatePersonModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  resetPage,
  companyId,
  allowDreamer = false,
  onSuccess,
  defaultFirstName,
  defaultLastName,
  defaultEmail,
  onCandidateDataConsumed
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const createPerson = useCreatePeopleMutation();
  const createWithDreamer = useCreatePersonWithDreamerMutation();
  const navigate = useNavigate();

  // `enabled` resolves async — gate the render on `loading` or the flag-OFF branch paints first and remounts the form.
  const { enabled: dreamerFlagEnabled, loading: flagLoading } = useFeatureFlag(
    FeatureFlags.CRM_8564_CREATE_PERSON_WITH_DREAMER
  );
  // allowDreamer gates both, so embedded call sites get neither the tab nor the spinner.
  const dreamerEnabled = allowDreamer && dreamerFlagEnabled;
  const dreamerFlagLoading = allowDreamer && flagLoading;
  const [activeTab, setActiveTab] = useState<DreamerMode>(DREAMER_MODE);
  const [dreamerEmail, setDreamerEmail] = useState("");
  const [showDreamerValidation, setShowDreamerValidation] = useState(false);
  const [isQueueing, setIsQueueing] = useState(false);
  const isDreamerTab = dreamerEnabled && activeTab === DREAMER_MODE;

  const [formSetValue, setFormSetValue] = useState<any>(null);
  const [formGetValues, setFormGetValues] = useState<any>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState<"companies" | "people">("people");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedPersonId, setSelectedPersonId] = useState<string>("");

  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [currentFieldForNewOption, setCurrentFieldForNewOption] = useState<{
    fieldId: string;
    fieldKey: string;
  } | null>(null);
  const [fieldResetKeys, setFieldResetKeys] = useState<Record<string, number>>(
    {}
  );

  const { data: entityFieldsResponse } = useEntityFields(EntityType.PERSON);
  const updateEntityField = useUpdateEntityField(EntityType.PERSON);

  const users = useBoundStore((state) => state.users.entities);

  // The enrichment-failure link carries only the email; its domain finds the company the failed attempt resolved.
  // isValidWebsite takes the email as is: it reads the part after "@" and rejects personal-mail and malformed domains.
  const candidateDomain =
    isOpen && !companyId && defaultEmail && isValidWebsite(defaultEmail)
      ? (extractDomain(defaultEmail) ?? "")
      : "";
  const companyDomainCheck = useCheckCompanyDuplicates(
    candidateDomain,
    candidateDomain.length > 0
  );
  // Exact domain match only, as the With Dreamer tab decides "Already in CRM".
  const domainMatchedCompanyId = candidateDomain
    ? (companyDomainCheck.data?.exactMatches?.[0]?.companyId ?? "")
    : "";
  // The form setter the domain match was last offered to: once per form, so a company the user picked is not overwritten.
  const domainPrefillSetterRef = useRef<unknown>(null);

  useEffect(() => {
    if (!domainMatchedCompanyId || typeof formSetValue !== "function") return;
    if (domainPrefillSetterRef.current === formSetValue) return;
    domainPrefillSetterRef.current = formSetValue;
    if (formGetValues?.()?.companyId) return;
    setSelectedCompanyId(domainMatchedCompanyId);
    formSetValue("companyId", domainMatchedCompanyId, { shouldValidate: true });
  }, [domainMatchedCompanyId, formSetValue, formGetValues]);

  useEffect(() => {
    if (companyId) {
      setSelectedCompanyId(companyId);
      if (typeof formSetValue === "function") {
        formSetValue("companyId", companyId, {
          shouldValidate: true
        });
      }
    }
  }, [companyId, formSetValue]);

  useEffect(() => {
    if (typeof formSetValue !== "function") return;
    if (defaultFirstName) {
      formSetValue("firstName", defaultFirstName, { shouldValidate: true });
    }
    if (defaultLastName) {
      formSetValue("lastName", defaultLastName, { shouldValidate: true });
    }
    if (defaultEmail) {
      formSetValue("emails", [defaultEmail], { shouldValidate: true });
    }
  }, [defaultFirstName, defaultLastName, defaultEmail, formSetValue]);

  // Deep links only come from the enrichment-failure notification — retrying Dreamer would fail the same way.
  useEffect(() => {
    if (isOpen && defaultEmail) {
      setActiveTab(MANUAL_MODE);
    }
  }, [isOpen, defaultEmail]);

  const handleCreateNewOption = async (optionName: string) => {
    if (!currentFieldForNewOption) return;

    try {
      const currentField = entityFieldsResponse?.fields?.find(
        (field) => field.id === currentFieldForNewOption.fieldId
      );

      if (!currentField?.config?.options) return;

      const currentOptions = currentField.config.options;
      const optionsArray = Array.isArray(currentOptions)
        ? currentOptions
        : Object.keys(currentOptions);

      const updatedOptionsArray = [...optionsArray, optionName];

      const updatedOptions = Array.isArray(currentOptions)
        ? updatedOptionsArray
        : updatedOptionsArray.reduce(
            (acc, option) => {
              acc[option] = option;
              return acc;
            },
            {} as Record<string, string>
          );

      await updateEntityField.mutateAsync({
        data: {
          id: currentFieldForNewOption.fieldId,
          config: {
            ...currentField.config,
            options: updatedOptions,
            allowUsersToAdd: true
          }
        },
        pathParams: {
          entityType: "people",
          fieldId: currentFieldForNewOption.fieldId
        }
      });

      // Auto-select the newly created option
      if (formSetValue && typeof formGetValues === "function") {
        const isMultiSelect = currentField.displayType === "multiSelect";
        const existingValues =
          formGetValues()[currentFieldForNewOption.fieldKey];
        const safeArray = Array.isArray(existingValues) ? existingValues : [];
        const updatedSelection = isMultiSelect
          ? [...safeArray, optionName]
          : optionName;

        formSetValue(currentFieldForNewOption.fieldKey, updatedSelection, {
          shouldValidate: true
        });

        // Add a small delay before resetting the field to ensure the form value is set
        setTimeout(() => {
          setFieldResetKeys((prev) => ({
            ...prev,
            [currentFieldForNewOption.fieldKey]:
              (prev[currentFieldForNewOption.fieldKey] || 0) + 1
          }));
        }, 100);
      }

      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  };

  const formFieldsContext: CreateFormNewContext = {
    users,
    userData: {} as UserData,
    selectedCompanyId,
    selectedPersonId,
    setSelectedCompanyId,
    setSelectedPersonId,
    setHasUserEnteredDealName: () => {},
    autoPopulateDealName: () => {},
    hasUserEnteredDealName: false,
    setView: (view) => {
      if (view === "companies" || view === "people") {
        setView(view);
      }
    },
    entityType: EntityType.PERSON,
    // New option creation context
    showCreateOptionModal,
    newOptionName,
    currentFieldForNewOption,
    fieldResetKeys,
    setShowCreateOptionModal,
    setNewOptionName,
    setCurrentFieldForNewOption,
    handleCreateNewOption
  };

  const baseSections = createFormNewSectionsFromAPI(
    entityFieldsResponse?.fields,
    t,
    formFieldsContext
  );

  const formSections: FormSectionProps[] = baseSections.map((section) => ({
    ...section,
    fields: section.fields?.map((field) => {
      let enhancedField = {
        ...field,
        labelPosition: "left" as const,
        layout: "flex" as const
      };

      if (field.name === "companyId" && companyId) {
        enhancedField.defaultValue = companyId;
      }
      if (field.name === "firstName" && defaultFirstName) {
        enhancedField.defaultValue = defaultFirstName;
      }
      if (field.name === "lastName" && defaultLastName) {
        enhancedField.defaultValue = defaultLastName;
      }
      if (field.name === "emails" && defaultEmail) {
        enhancedField.defaultValue = [defaultEmail];
      }

      return enhancedField;
    })
  }));

  // Both tabs stay mounted, so closing checks both; distinct key so the form's own `emails` field isn't clobbered.
  const { handleCloseAttempt, warningDialogProps } = useUnsavedChanges(
    () => ({
      ...(formGetValues?.() || {}),
      ...(dreamerEmail.trim() ? { dreamerEmail } : {})
    }),
    formSections
  );

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
    } else {
      formRef.current?.reset();
      setFormSetValue(null);
      setFormGetValues(null);
      setView("people");
      setSelectedCompanyId("");
      setSelectedPersonId("");
      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
      setFieldResetKeys({});
      setIsFormValid(false);
      setActiveTab(DREAMER_MODE);
      setDreamerEmail("");
      setShowDreamerValidation(false);
      setIsQueueing(false);
      domainPrefillSetterRef.current = null;
      // Drop the deep-link prefill so reopening the modal starts clean.
      onCandidateDataConsumed?.();
    }
    // onCandidateDataConsumed omitted: a fresh closure per parent render would re-run the reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Stable backend codes; 403/404 are dead ends and must not read as transient (404 = backend flag off).
  const dreamerQueueErrorTitle = (error: unknown): string => {
    const detail = isAxiosError(error) ? error.response?.data?.detail : undefined;
    switch (detail) {
      case "user_not_provisioned":
        return t("people.createModal.withDreamer.errorNotProvisioned");
      case "invalid_email":
        return t("people.createModal.withDreamer.invalidEmail");
      case "not_found":
        return t("people.createModal.withDreamer.errorNotAvailable");
      case "person_exists":
        return t("people.createModal.withDreamer.errorPersonExists");
      default:
        return t("people.createModal.withDreamer.queueError");
    }
  };

  const handleDreamerSubmit = async () => {
    setShowDreamerValidation(true);
    const trimmedEmail = dreamerEmail.trim();
    if (!emailRegex.test(trimmedEmail) || isQueueing) {
      return;
    }
    setIsQueueing(true);
    // mutateAsync rejects even with onError supplied — unguarded, the toast never renders.
    try {
      await createWithDreamer.mutateAsync({ email: trimmedEmail });
      toast.success(
        <ToastBody title={t("people.createModal.withDreamer.queued")} />,
        { duration: 5000 }
      );
      setIsOpen(false);
    } catch (error) {
      setIsQueueing(false);
      toast.error(
        <ToastBody
          title={dreamerQueueErrorTitle(error)}
          actions={
            <IconButton
              size="md"
              variant="iconNoBg"
              iconColor="secondary"
              onClick={() => toast.dismiss()}
            >
              <Close />
            </IconButton>
          }
        />,
        { icon: null }
      );
    }
  };

  const handleSubmit = async (data: PersonData) => {
    setIsSubmitting(true);
    const transformFormData: PersonData = {
      ...data,
      ...mapEmptyToNull(buildMapEmptyToNullObject(baseSections, data)),
      status: PersonStatus.NEW,
      referenceability: PersonReferenceabilityLevel.UNKNOWN
    };

    await createPerson.mutateAsync(transformFormData, {
      onSuccess: async (response) => {
        resetPage?.();
        onSuccess?.(response.id, data.firstName);
        toast.success(
          <ToastBody
            title={`${data.firstName} ${data.lastName} ${t("people.createModal.success")}`}
            actions={
              <Button
                className="size-fit p-1"
                onClick={() => {
                  toast.dismiss();
                  navigate(`/people/${response?.id}`);
                }}
                variant="primary"
              >
                {t("people.createModal.view")}
              </Button>
            }
          />,
          {
            duration: 5000
          }
        );
        setIsOpen(false);
      },
      onError: (_error: Error) => {
        setIsSubmitting(false);
        toast.error(
          <ToastBody
            title={t("people.createModal.error")}
            actions={
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            }
          />,
          {
            icon: null
          }
        );
      }
    });
  };

  const handleCancel = () => {
    handleCloseAttempt(() => {
      setIsOpen(false);
    });
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
    if (typeof formMethods.getValues === "function") {
      setFormGetValues(() => formMethods.getValues);
    }
  };

  const onCompanyCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setSelectedCompanyId(id);
    if (typeof formSetValue === "function") {
      formSetValue("companyId", id, {
        shouldValidate: true
      });
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={createUnsavedChangesModalSetter(
          setIsOpen,
          handleCloseAttempt
        )}
        data-test="create-person-modal"
        title={
          <Text as="div" variant="lgSubtitleH1" className="normal-case">
            {t("people.createModal.title")}
          </Text>
        }
        content={
          <div
            className={cn(
              "flex flex-col w-full p-6 overflow-y-auto",
              // g6 (24px) between the switch and the Manually form; the Dreamer tab keeps its own rhythm.
              isDreamerTab ? "gap-3" : "gap-6",
              isDreamerTab ? "max-h-[60vh]" : "h-[60vh]"
            )}
            data-test="create-person-modal-content"
          >
            {dreamerEnabled && !dreamerFlagLoading ? (
              <DreamerModeTabs
                labels={{
                  withDreamer: t("dreamerCreate.withDreamerTab"),
                  manually: t("dreamerCreate.manuallyTab")
                }}
                value={activeTab}
                onChange={setActiveTab}
                dataTest="create-person-modal-tabs"
              />
            ) : null}

            {dreamerFlagLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <>
                {isDreamerTab ? (
                  <CreatePersonWithDreamer
                    email={dreamerEmail}
                    setEmail={setDreamerEmail}
                    showValidation={showDreamerValidation}
                  />
                ) : null}
                {/* Hidden, not unmounted: remounting react-hook-form wipes typed values on a tab peek. */}
                <div className={cn(isDreamerTab && "hidden")}>
                  <FormNew
                    sections={formSections}
                    isFormValid={(isValid) => {
                      setIsFormValid(isValid);
                    }}
                    onSubmit={handleSubmit}
                    formRef={formRef}
                    onSetValue={handleSetValue}
                    data-test="create-person-modal-form"
                  />
                </div>
              </>
            )}
          </div>
        }
        footer={
          <div
            className="flex justify-end gap-2"
            data-test="create-person-modal-footer"
          >
            <Button
              data-test="create-person-modal-cancel-button"
              variant="secondary"
              disabled={isQueueing}
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            {/* Footer mirrors the content's loading gate — no manual submit while the flag decides the tab. */}
            {dreamerFlagLoading ? null : isDreamerTab ? (
              <Button
                data-test="create-person-modal-dreamer-submit-button"
                disabled={isQueueing || dreamerEmail.trim().length === 0}
                onClick={handleDreamerSubmit}
              >
                {t("people.createModal.withDreamer.submit")}
              </Button>
            ) : (
              <Button
                data-test="create-person-modal-submit-button"
                disabled={!isFormValid || isSubmitting}
                onClick={handleCreateNew}
              >
                {t("people.createModal.createNew")}
              </Button>
            )}
          </div>
        }
        contentContainerClassname="w-[640px]"
      />
      <CreateCompanyModal
        isOpen={view === "companies"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("people");
          }
        }}
        onSuccess={onCompanyCreation}
      />
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => setShowCreateOptionModal(false)}
        onCreateOption={handleCreateNewOption}
        title={t("createNewOption.title", { optionName: newOptionName })}
        description={t("createNewOption.description")}
        createButtonText={t("createNewOption.create")}
        cancelButtonText={t("cancel")}
        optionName={newOptionName}
        data-test="create-person-modal-new-option"
      />
      <UnsavedChangesWarningDialog
        {...warningDialogProps}
        data-test="create-person-modal-unsaved-warning"
      />
    </>
  );
};

export default CreatePersonModal;
